/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { queryCache } from 'react-query';

import { QueryKeys } from '../constants/queryKeys';
import { store } from '../store';
import { authRefreshUser } from '../store/auth/actions';
import { fetchOrganizations } from '../store/organization/actions';
import { fetchProjectsByUser } from '../store/projects/actions';
import { setThumbnailsVersion } from '../store/resources/actions';
import { EntityId } from '../types/api/EntityId';
import { ClusterAccessor } from '../types/common/Accessors';
import {
  adminFetchPlans,
  fetchAdminJiraConfig,
  fetchAdminJiraTickets,
  fetchCluster,
  fetchClusterDetails,
  fetchClusterLogicalBackups,
  fetchCredentialsOfCluster,
  fetchDatabaseListOfCluster,
  fetchDatasetImports,
  fetchDatasets,
  fetchJiraConfig,
  fetchJiraRequestComments,
  fetchJiraRequests,
  fetchLogicalBackupStatus,
  fetchNodeListOfCluster,
  fetchPgHbaOfCluster,
  fetchProjectsWithClusters,
  fetchStorageOfCluster,
  fetchSupportPlans,
} from './queryFns';

export const reactToCreateOrganization = () => {
  return Promise.all([
    store.dispatch(fetchOrganizations()),
    queryCache.refetchQueries([QueryKeys.fetchDashboardForUser]),
  ]);
};
export const reactToUpdateOrganization = ({ organizationId }: { organizationId: EntityId }) => {
  return Promise.all([
    Promise.resolve(store.dispatch(fetchOrganizations())),
    queryCache.refetchQueries([QueryKeys.fetchOrganizationById, { organizationId }]),
  ]);
};
export const reactToUpdateOrganizationThumbnail = ({ organizationId }: { organizationId: string }) => {
  return Promise.all([
    Promise.resolve(store.dispatch(setThumbnailsVersion(Date.now()))),
    queryCache.refetchQueries([QueryKeys.fetchOrganizationById, { organizationId }]),
  ]);
};

export const reactToDeleteOrganization = () => {
  store.dispatch(fetchOrganizations());
};

export const reactToCreateProject = (params?: { organizationId: EntityId }) => {
  if (params) {
    const { organizationId } = params;
    return Promise.all([
      queryCache.refetchQueries([QueryKeys.fetchProjectsWithClusters, { organizationId }]),
      queryCache.refetchQueries([QueryKeys.fetchDashboardForOrganization, { organizationId }]),
    ]);
  } else {
    store.dispatch(fetchProjectsByUser());
    return Promise.resolve();
  }
};
export const reactToUpdateProject = ({
  organizationId,
  projectId,
}: {
  organizationId: EntityId;
  projectId: EntityId;
}) => {
  const promises: Promise<any>[] = [queryCache.refetchQueries([QueryKeys.fetchProject, { organizationId, projectId }])];
  promises.push(queryCache.refetchQueries([QueryKeys.fetchProjectsWithClusters, { organizationId }]));
  promises.push(queryCache.refetchQueries([QueryKeys.fetchProjectsOfOrganization, { organizationId }]));
  return Promise.all(promises);
};
export const reactToUpdateProjectThumbnail = ({
  organizationId,
  projectId,
}: {
  organizationId: EntityId;
  projectId: EntityId;
}) => {
  return Promise.all([
    Promise.resolve(store.dispatch(setThumbnailsVersion(Date.now()))),
    queryCache.refetchQueries([QueryKeys.fetchProject, { organizationId, projectId }]),
    queryCache.refetchQueries([QueryKeys.fetchProjectsWithClusters, { organizationId }]),
  ]);
};

export const reactToDeleteProject = (params?: { organizationId: EntityId }) => {
  if (params) {
    const { organizationId } = params;
    return Promise.all([
      queryCache.refetchQueries([QueryKeys.fetchDashboardForOrganization, { organizationId }]),
      queryCache.refetchQueries([QueryKeys.fetchProjectsOfOrganization, { organizationId }]),
      queryCache.refetchQueries([QueryKeys.fetchProjectsWithClusters, { organizationId }]),
    ]);
  } else {
    store.dispatch(fetchProjectsByUser());
    return Promise.resolve();
  }
};

export const reactToCreateCluster = ({
  organizationId,
  projectId,
}: {
  organizationId: EntityId;
  projectId: EntityId;
}) => {
  return Promise.all([
    queryCache.refetchQueries(QueryKeys.fetchDashboardForUser),
    queryCache.refetchQueries([QueryKeys.fetchProjectsWithClusters, { organizationId }]),
    queryCache.refetchQueries([QueryKeys.fetchDashboardForOrganization, { organizationId }]),
    queryCache.refetchQueries([QueryKeys.fetchProducts, { organizationId, projectId }]),
  ]);
};
export const reactToUpdateCluster = ({
  organizationId,
  projectId,
  clusterId,
}: {
  organizationId: EntityId;
  projectId: EntityId;
  clusterId: EntityId;
}) => {
  const promises = [
    queryCache.refetchQueries([QueryKeys.fetchProjectsWithClusters, { organizationId }]),
    queryCache.refetchQueries(QueryKeys.fetchDashboardForUser),
    queryCache.refetchQueries([QueryKeys.fetchDashboardForOrganization, { organizationId }]),
    queryCache.refetchQueries([QueryKeys.fetchProducts, { organizationId, projectId }]),
    queryCache.refetchQueries([QueryKeys.fetchCluster, { organizationId, projectId, clusterId }]),
    queryCache.refetchQueries([fetchProjectsWithClusters.key, { organizationId }]),
    queryCache.refetchQueries([fetchClusterDetails.key, { organizationId, projectId, clusterId }]),
    queryCache.refetchQueries([fetchNodeListOfCluster.key, { organizationId, projectId, clusterId }]),
    queryCache.refetchQueries([fetchStorageOfCluster.key, { organizationId, projectId, clusterId }]),
    queryCache.refetchQueries([QueryKeys.fetchDatasetImports, { organizationId, projectId, clusterId }]),
    queryCache.refetchQueries([fetchPgHbaOfCluster.key, { organizationId, projectId, clusterId }]),
    queryCache.refetchQueries([
      QueryKeys.fetchContinuousBackupOldestRestorePoint,
      { organizationId, projectId, clusterId },
    ]),
  ];

  return Promise.all(promises);
};

export const reactToInviteUserToOrganization = ({ organizationId }: { organizationId: EntityId }) => {
  return Promise.all([
    queryCache.refetchQueries([QueryKeys.fetchOrganizationUsers, { organizationId }]),
    queryCache.refetchQueries([QueryKeys.fetchOrganizationInvitations, { organizationId }]),
    queryCache.refetchQueries([QueryKeys.fetchOrganizationRegistrationInvitations, { organizationId }]),
    queryCache.refetchQueries([QueryKeys.fetchAllActiveUsers]),
  ]);
};

export const reactToUpdateClusterPassword = ({
  organizationId,
  projectId,
  clusterId,
}: {
  organizationId: EntityId;
  projectId: EntityId;
  clusterId: EntityId;
}) => {
  return Promise.all([
    queryCache.refetchQueries([QueryKeys.fetchCluster, { organizationId, projectId, clusterId }]),
    queryCache.refetchQueries([fetchCredentialsOfCluster.key, { organizationId, projectId, clusterId }]),
  ]);
};

export const reactToResolveOrganizationInvitation = ({ organizationId }: { organizationId: EntityId }) => {
  return Promise.all([
    Promise.resolve(store.dispatch(authRefreshUser())),
    queryCache.refetchQueries([QueryKeys.fetchDashboardForUser]),

    queryCache.refetchQueries([QueryKeys.fetchOrganizationById, { organizationId }]),
    queryCache.refetchQueries([QueryKeys.fetchDashboardForOrganization, { organizationId }]),
  ]);
};

export const reactToRemoveUserFromOrganization = ({ organizationId }: { organizationId: EntityId }) => {
  return Promise.all([queryCache.refetchQueries([QueryKeys.fetchOrganizationUsers, { organizationId }])]);
};

export const reactToRemoveLogicalBackup = ({
  organizationId,
  clusterId,
  projectId,
}: {
  organizationId: EntityId;
  clusterId: EntityId;
  projectId: EntityId;
}) => {
  return Promise.all([
    queryCache.refetchQueries([fetchClusterLogicalBackups.key, { organizationId, clusterId, projectId }]),
  ]);
};

export const reactToCreateLogicalBackup = ({
  organizationId,
  clusterId,
  projectId,
}: {
  organizationId: EntityId;
  clusterId: EntityId;
  projectId: EntityId;
}) => {
  return Promise.all([
    queryCache.refetchQueries([fetchClusterLogicalBackups.key, { organizationId, clusterId, projectId }]),
    queryCache.refetchQueries([fetchLogicalBackupStatus.key, { organizationId, clusterId, projectId }]),
  ]);
};

export const reactToTriggerLogicalBackup = ({
  organizationId,
  clusterId,
  projectId,
}: {
  organizationId: EntityId;
  clusterId: EntityId;
  projectId: EntityId;
}) => {
  return Promise.all([
    queryCache.refetchQueries([fetchLogicalBackupStatus.key, { organizationId, clusterId, projectId }]),
  ]);
};

export const reactToCancelLogicalBackup = ({
  organizationId,
  clusterId,
  projectId,
}: {
  organizationId: EntityId;
  clusterId: EntityId;
  projectId: EntityId;
}) => {
  return Promise.all([
    queryCache.refetchQueries([fetchClusterLogicalBackups.key, { organizationId, clusterId, projectId }]),
    queryCache.refetchQueries([fetchLogicalBackupStatus.key, { organizationId, clusterId, projectId }]),
  ]);
};

export const reactToImportDatasets = ({
  organizationId,
  projectId,
  clusterId,
}: {
  organizationId: EntityId;
  projectId: EntityId;
  clusterId: EntityId;
}) => {
  return Promise.all([
    queryCache.refetchQueries([QueryKeys.fetchDatasetImports, { organizationId, clusterId, projectId }]),
  ]);
};

export const reactToUpdateUserThumbnail = () => {
  return Promise.all([
    Promise.resolve(store.dispatch(setThumbnailsVersion(Date.now()))),
    Promise.resolve(store.dispatch(authRefreshUser())),
  ]);
};

export const reactToUpdateAdminDatasetCategories = () => {
  return Promise.all([
    queryCache.refetchQueries([QueryKeys.fetchAdminDatasetsCategories]),
    queryCache.refetchQueries([QueryKeys.fetchDatasets]),
  ]);
};

export const reactToUpdateAdminDatasets = () => {
  return Promise.all([
    queryCache.refetchQueries([QueryKeys.fetchAdminDatasets]),
    queryCache.refetchQueries([QueryKeys.fetchDatasets]),
  ]);
};

export const reactToUpdateUserStatusFromAdminPanel = () => {
  return Promise.all([queryCache.refetchQueries(QueryKeys.adminFetchUsers)]);
};

export const reactToRemoveClusterFromAdminTable = () => {
  return Promise.all([queryCache.refetchQueries(QueryKeys.adminFetchOrphans)]);
};

export const reactToImportClusterDataSetsError = (accessor: ClusterAccessor) => {
  return Promise.all([
    queryCache.refetchQueries([fetchDatasets.key]),
    queryCache.refetchQueries([fetchStorageOfCluster.key, accessor]),
    queryCache.refetchQueries([fetchDatabaseListOfCluster.key, accessor]),
    queryCache.refetchQueries([fetchDatasetImports.key, accessor]),
  ]);
};

export const reactToAdminCreatePlanStorage = () => {
  return Promise.all([queryCache.refetchQueries(adminFetchPlans.key)]);
};

export const reactToAdminCreatePlanCompute = reactToAdminCreatePlanStorage;

export const reactToAdminDeletePlanStorage = () => {
  return Promise.all([queryCache.refetchQueries(adminFetchPlans.key)]);
};

export const reactToAdminDeletePlanCompute = reactToAdminDeletePlanStorage;

export const reactToAdminSupportPlanAction = () => {
  return Promise.all([queryCache.refetchQueries(fetchSupportPlans.key)]);
};

export const reactToAdminUpdatePlanStorage = () => {
  return Promise.all([queryCache.refetchQueries(adminFetchPlans.key)]);
};

export const reactToAdminUpdatePlanCompute = reactToAdminUpdatePlanStorage;

export const reactToCreateClusterFromBackup = ({
  organizationId,
  projectId,
  sourceClusterId,
}: {
  organizationId: EntityId;
  projectId: EntityId;
  sourceClusterId: EntityId;
}) => {
  return Promise.all([
    queryCache.refetchQueries([fetchCluster.key, { organizationId, projectId, clusterId: sourceClusterId }]),
    queryCache.refetchQueries(QueryKeys.fetchDashboardForUser),
    queryCache.refetchQueries([QueryKeys.fetchProjectsWithClusters, { organizationId }]),
    queryCache.refetchQueries([QueryKeys.fetchDashboardForOrganization, { organizationId }]),
    queryCache.refetchQueries([QueryKeys.fetchProducts, { organizationId, projectId }]),
  ]);
};

export const reactToRestartClusterNode = ({
  organizationId,
  projectId,
  clusterId,
}: {
  organizationId: EntityId;
  projectId: EntityId;
  clusterId: EntityId;
}) => {
  return Promise.all([
    queryCache.refetchQueries([fetchNodeListOfCluster.key, { organizationId, projectId, clusterId }]),
  ]);
};

export const reactToAdminUpdateJiraConfig = () => {
  return Promise.all([
    queryCache.refetchQueries(fetchAdminJiraConfig.key),
    queryCache.refetchQueries(fetchJiraConfig.key),
  ]);
};

export const reactToCreateJiraIssue = () => {
  return Promise.all([
    queryCache.refetchQueries(fetchJiraRequests.key),
    queryCache.refetchQueries(fetchAdminJiraTickets.key),
  ]);
};

export const reactToCreateJiraComment = () => {
  return Promise.all([queryCache.refetchQueries([fetchJiraRequestComments.key])]);
};
